import NotFound from '../components/NotFound';
import { SITE_PHOTOS, servicePhoto } from '../sitePhotos';
import ConstructionPhotos from '../components/ConstructionPhotos';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';
import { publicCollection } from '../lib/publicContent';
import { useSiteInfo } from '../context/SiteContext';
import Sidebar from '../components/Sidebar';
import AdBlock from '../components/AdBlock';
import { SERVICES } from '../data';
import { Loader2 } from 'lucide-react';
import CommentSection from '../components/CommentSection';
import FacebookComments from '../components/FacebookComments';
import { ServiceSchema } from '../components/SEO';
import { optimizeDataImages, cleanQuillHtml } from '../lib/imageCompression';

export default function ServiceDetail() {
  const { siteInfo, services } = useSiteInfo();
  const { slug } = useParams<{ slug: string }>();
  
  const [service, setService] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchService = async () => {
      setLoading(true);
      const minDelay = new Promise(resolve => setTimeout(resolve, 220));
      try {
        if (!slug) return;

        // 1. Kiểm tra trong SiteContext trước
        const contextMatch = services.find(s => s.slug === slug || s.id === slug);
        if (contextMatch) {
          await minDelay;
          if (!cancelled) {
            setService(optimizeDataImages(contextMatch));
            setLoading(false);
          }
          return;
        }

        // 2. Nếu không có trong Context, thực hiện fetch Firestore
        const match = (await publicCollection('services')).find(s=>s.slug===slug || s.id===slug);
        if (!cancelled) setService(match || SERVICES.find(s=>s.slug===slug || s.id===slug) || null);
      } catch (error) {
        console.error("Error fetching service detail:", error);
        await minDelay;
        if (cancelled) return;
        const staticMatch = SERVICES.find(s => s.slug === slug || s.id === slug);
        setService(staticMatch || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchService();
    return () => { cancelled = true; };
  }, [slug, services]);

  if (!loading && !service) return <NotFound />;

  const serviceImage = service ? servicePhoto(service.id || service.slug, SERVICES.findIndex(item => item.id === service.id), service.image) : undefined;

  const serviceMetaImage = serviceImage?.startsWith("/") ? `https://www.khoancocnhoivietnam.vn${serviceImage}` : serviceImage;
  const canonicalUrl = service ? `https://www.khoancocnhoivietnam.vn/${service.slug}` : 'https://www.khoancocnhoivietnam.vn';
  const serviceJsonLd = service ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "url": canonicalUrl,
    "image": serviceMetaImage,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Công Ty TNHH Phát Triển Xây Dựng Nền Móng Việt Nam",
      "url": "https://www.khoancocnhoivietnam.vn",
      "telephone": "0968387568",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Hà Nội",
        "addressCountry": "VN"
      }
    },
    "areaServed": {
      "@type": "AdministrativeArea",
      "name": "Hà Nội"
    }
  } : null;

  const breadcrumbJsonLd = service ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Trang chủ",
        "item": "https://www.khoancocnhoivietnam.vn"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": service.title,
        "item": canonicalUrl
      }
    ]
  } : null;

  return (
    <>
      {service && (
        <Helmet>
          <title>{service.title} - Khoan Cọc Nhồi Hà Nội | {siteInfo.name}</title>
          <meta name="description" content={service.description} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={`${service.title} - ${siteInfo.name}`} />
          <meta property="og:description" content={service.description} />
          <meta property="og:image" content={serviceMetaImage} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="Nền Móng Việt Nam" />
          <meta property="og:locale" content="vi_VN" />
          <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
          <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        </Helmet>
      )}
      {service && (
        <ServiceSchema
          name={service.title}
          description={service.description}
          url={canonicalUrl}
          image={serviceMetaImage}
          provider={{
            name: siteInfo.name,
            url: siteInfo.baseUrl || 'https://khoancocnhoivietnam.vn',
          }}
          areaServed="Hà Nội, Bắc Ninh, Hưng Yên, Hà Nam"
          priceRange="Liên hệ"
        />
      )}

      <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row gap-8">
        {/* Main Content */}
        <div className="w-full md:w-3/4 min-w-0">
          {loading || !service ? (
            <div className="bg-white rounded-lg shadow-sm border min-h-[60vh] flex flex-col items-center justify-center p-12">
              <Loader2 className="animate-spin text-primary mb-3" size={36} />
              <span className="text-sm text-gray-400 font-medium">Đang tải nội dung...</span>
            </div>
          ) : (
            <motion.div
              key={service.slug}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Breadcrumb */}
              <nav className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
                <a href="/" className="hover:text-primary whitespace-nowrap">Trang chủ</a>
                <span>/</span>
                <span className="text-gray-800 font-medium break-words max-w-full">{service.title}</span>
              </nav>

              <article className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-sm border">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-5 md:mb-6 leading-snug">
                  {service.title}
                </h1>
                
                <div className="mb-6 rounded-lg overflow-hidden">
                  <img 
                    src={serviceImage} 
                    alt={service.title} 
                    className="w-full h-auto max-h-[500px] object-contain bg-gray-50"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = SITE_PHOTOS[0].url;
                    }}
                  />
                </div>

                <div className="prose max-w-none text-gray-700 leading-relaxed break-words">
                  <p className="text-lg font-medium mb-4">
                    {service.description}
                  </p>
                  
                  {/* Render dynamic content if available, otherwise fallback to generic content */}
                  {(service as any).content ? (
                    <div 
                      className="article-content ql-editor break-words"
                      dangerouslySetInnerHTML={{ __html: cleanQuillHtml((service as any).content) }} 
                    />
                  ) : (
                    <>
                      <h3 className="text-xl font-bold mt-8 mb-4">1. Giới thiệu dịch vụ</h3>
                      <p className="mb-4">
                        CÔNG TY TNHH PHÁT TRIỂN XÂY DỰNG NỀN MÓNG VIỆT NAM tự hào là đơn vị cung cấp dịch vụ <strong>{service.title}</strong> chuyên nghiệp hàng đầu. Với nhiều năm kinh nghiệm trong ngành xây dựng, chúng tôi đã thực hiện hàng trăm dự án lớn nhỏ, mang lại sự an tâm tuyệt đối cho khách hàng.
                      </p>

                      <h3 className="text-xl font-bold mt-8 mb-4">2. Quy trình thi công</h3>
                      <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Khảo sát địa chất và mặt bằng thi công thực tế.</li>
                        <li>Lập phương án thi công và báo giá chi tiết.</li>
                        <li>Tập kết máy móc, thiết bị và vật tư đến công trình.</li>
                        <li>Tiến hành thi công theo đúng bản vẽ thiết kế và tiêu chuẩn kỹ thuật.</li>
                        <li>Nghiệm thu, bàn giao và bảo hành công trình.</li>
                      </ul>

                      <h3 className="text-xl font-bold mt-8 mb-4">3. Cam kết của chúng tôi</h3>
                      <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Đảm bảo tiến độ thi công nhanh chóng, đúng cam kết.</li>
                        <li>Chất lượng công trình đạt tiêu chuẩn cao nhất.</li>
                        <li>Chi phí cạnh tranh, minh bạch, không phát sinh.</li>
                        <li>Đảm bảo an toàn lao động và vệ sinh môi trường.</li>
                      </ul>
                    </>
                  )}

                  <div className="bg-blue-50 p-6 rounded-lg mt-8 border border-blue-100">
                    <h4 className="font-bold text-lg text-primary mb-2">Liên hệ tư vấn & báo giá:</h4>
                    <p><strong>Hotline:</strong> 0968 387 568 (Mr. Dự)</p>
                    <p><strong>Email:</strong> nenmongvietnam.kcn@gmail.com</p>
                  </div>
                  
                  <ConstructionPhotos />
                  <AdBlock position="service_bottom" className="mt-8 pt-6" />
                  
                  {/* Comment Section */}
                  {siteInfo.enableComments && (siteInfo.commentType === 'firebase' || siteInfo.commentType === 'both') && (
                    <CommentSection 
                      postId={service.slug} 
                      postType="service" 
                      enabled={true}
                    />
                  )}
                  {siteInfo.enableComments && (siteInfo.commentType === 'facebook' || siteInfo.commentType === 'both') && (
                    <FacebookComments 
                      url={`${siteInfo.baseUrl}/${service.slug}`}
                      enabled={true}
                      facebookCommentCode={siteInfo.facebookCommentCode}
                    />
                  )}
                </div>
              </article>
            </motion.div>
          )}
        </div>

        {/* Sidebar */}
        <div className="w-full md:w-1/4 md:shrink-0">
          <Sidebar />
        </div>
      </div>
    </>
  );
}

